import { z } from 'zod';

import { AssetType } from '../enums';

const IBaseAssetSchema = z.object({
  id: z.string(),
  title: z.string(),
  type: z.nativeEnum(AssetType),
  path: z.string(),

  //Optional
  //constructed by front end (casePath + path)
  url: z.string().optional(),
  /*
    constructed by front end
      if video: (casePath + thumbPath)
      if image: url
    */
  thumbUrl: z.string().optional(),
});

export enum VideoQuality {
  P360 = '360p',
  P480 = '480p',
  P720 = '720p',
  P1080 = '1080p',
}

// NOTE: For videos, we optionally support multiple quality sources while
// keeping backward compatibility with a single `path`.
const videoSourceSchema = z.object({
  quality: z.nativeEnum(VideoQuality),
  path: z.string(),

  // Optional - constructed by front end (casePath + path)
  url: z.string().optional(),
  mimeType: z.string().optional(),
});

const IImageAssetSpecificSchema = IBaseAssetSchema.and(
  z.object({
    type: z.literal(AssetType.IMAGE),
  })
);

const IVideoAssetSpecificSchema = IBaseAssetSchema.and(
  z.object({
    type: z.literal(AssetType.VIDEO),
    thumbPath: z.string(),
    // Optional multi-quality sources. `path` remains valid for single-source videos.
    sources: z.array(videoSourceSchema).optional(),
  })
);

export const assetSchema = z.union([IImageAssetSpecificSchema, IVideoAssetSpecificSchema]); //IImageAssetSpecificSchema.or(IVideoAssetSpecificSchema);

export type Asset = z.infer<typeof assetSchema>;
export type VideoSource = z.infer<typeof videoSourceSchema>;

// Force rebuild

// Type guards for better type safety
export const isVideoAsset = (asset: Asset): asset is z.infer<typeof IVideoAssetSpecificSchema> => {
  return asset.type === AssetType.VIDEO;
};

export const hasVideoSources = (
  asset: Asset
): asset is z.infer<typeof IVideoAssetSpecificSchema> & {
  sources: VideoSource[];
} => {
  return isVideoAsset(asset) && Array.isArray(asset.sources) && asset.sources.length > 0;
};
